import React from "react";
import { storage } from "../firebase/config";
import { PlayerContextType, QUEUE_ITEM } from "../libs/Interfaces";
import { useAuth } from "./AuthContext";

const defaultProviderValue: PlayerContextType = {
    active: false,
    repeat: undefined,
    setRepeat: (repeat) => console.log(repeat),
    pauseorplay: (_item) => console.log(_item),
    removeoraddtoplaylist: (_item) => console.log(_item),
    stats: {
        max: 0,
        min: 0,
        current: 0,
        text: "00:00"
    },
    status: "PAUSED",
    currentIndex: 0,
    playlistName: "Currently Playing",
    queue: [],
    play: () => console.log("played"),
    pause: () => console.log("paused"),
    next: () => console.log("next"),
    prev: () => console.log("prev"),
    seek: (time) => console.log(time),
    setPlaylist: (queue, name, item) => console.log(queue, name, item),
    shuffle: () => console.log("shuffle"),
    playItemFromQueue: (item) => console.log("item")
};

const PlayerContext = React.createContext<PlayerContextType>(defaultProviderValue);

export function usePlayer() {
    return React.useContext(PlayerContext);
}

function shuffleList<T>(array: T[]) {
    var currentIndex = array.length, temporaryValue, randomIndex;
    // While there remain elements to shuffle...
    while (0 !== currentIndex) {
        // Pick a remaining element...
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;
        // And swap it with the current element.
        temporaryValue = array[currentIndex];
        array[currentIndex] = array[randomIndex];
        array[randomIndex] = temporaryValue;
    }
    return array;
}

export function timetext(s: number) {
    if (isNaN(s)) return "00 : 00";
    s = Math.floor(s);
    let mins = Math.floor(s / 60);
    let secs = s % 60;
    let text = '';
    if (mins < 10) text += "0";
    text += `${mins} : `;
    if (secs < 10) text += "0";
    text += `${secs}`;
    return text;
};

interface PlayerControllerInterface {
    queue: QUEUE_ITEM[],
    name: string;
    index: number;
    status: "PLAYING" | "BUFFERING" | "PAUSED";
    repeat: "ALL" | "ONE" | undefined;
    stats: {
        max: number;
        min: number;
        current: number;
        text: string;
    };
};

class PlayerController {
    audio = new Audio();
    onUpdate = (updatedValue: PlayerControllerInterface) => console.log(updatedValue);
    data: PlayerControllerInterface = {
        queue: [],
        name: "Currently Playing",
        index: 0,
        status: "PAUSED",
        repeat: undefined,
        stats: {
            max: 0,
            min: 0,
            current: 0,
            text: "00:00"
        },
    };
    constructor(updater?: (updatedValue: PlayerControllerInterface) => void) {
        this.audio.preload = "metadata";
        if (updater) this.onUpdate = updater;
        this.addEventListeners();
    };
    updateData = (data: PlayerControllerInterface) => {
        this.data = data;
        this.onUpdate(data);
    };
    play = async (_indexChanged: boolean = false) => {
        if (this.data.queue.length <= 0) return;
        if (this.data.index < 0 || this.data.index >= this.data.queue.length) return;
        if (this.data.status === "PAUSED" && !_indexChanged) return this.audio.play();
        let item = this.data.queue[this.data.index];
        this.audio.src = await storage.ref(`songs/${item.artistId}/${item.id}.mp3`).getDownloadURL();;
        this.audio.load();
        this.audio.play();
    };
    pause = () => {
        if (this.data.queue.length <= 0) return;
        if (this.data.index < 0 || this.data.index >= this.data.queue.length) return;
        if (this.data.status === "PAUSED") return;
        this.audio.pause();
    };
    next = () => {
        this.pause();
        if (this.data.queue.length === 0) return;
        let index = (this.data.index + 1) % this.data.queue.length;
        let gonna_play = true;
        if (this.data.index >= this.data.queue.length && typeof this.data.repeat === "undefined") {
            gonna_play = false;
        }
        if (this.data.repeat === "ONE") {
            this.updateData({ ...this.data, repeat: undefined });
        }
        this.updateData({ ...this.data, index: index });
        if (gonna_play) this.play(true);
    }
    prev = () => {
        this.pause();
        if (this.data.queue.length === 0) return;
        let index = this.data.index - 1;
        if (index < 0) {
            index = this.data.queue.length + index;
        };
        let gonna_play = true;
        if (this.data.index <= 0 && typeof this.data.repeat === "undefined") {
            gonna_play = false;
        }
        if (this.data.repeat === "ONE") {
            this.updateData({ ...this.data, repeat: undefined });
        }
        this.updateData({ ...this.data, index: index });
        if (gonna_play) this.play(true);
    };
    pauseorplay = (_item: QUEUE_ITEM) => {
        let _exists = this.data.queue.find(x => x.id === _item.id);
        if (_exists) {
            let _isPlaying = this.data.queue[this.data.index];
            if (_isPlaying.id === _item.id) {
                if (this.data.status === "PLAYING") this.pause();
                else if (this.data.status === "PAUSED") this.play();
            } else {
                this.playItemFromQueue(_item);
            };
        }
        else {
            let _queue = [_item];
            this.setPlaylist(_queue, "Now Playing", _item);
        };
    };
    removeoraddtoplaylist = (_item: QUEUE_ITEM) => {
        let _exists = this.data.queue.find(x => x.id === _item.id);
        if (_exists) {
            this.removeFromPlaylist(_exists);
        }
        else {
            this.addToPlaylist(_item);
        };
    };
    removeFromPlaylist = (_item: QUEUE_ITEM) => {
        let _queue = this.data.queue;
        let _isPlaying = this.data.queue[this.data.index];
        if (_item.id === _isPlaying.id) {
            if (this.data.queue.length === 1) {
                this.pause();
            }
            else this.next();
        }
        _queue = _queue.filter(x => x.id !== _item.id);
        let _curindex = _queue.findIndex(x => x.id === this.data.queue[this.data.index].id);
        this.updateData({
            ...this.data,
            queue: _queue,
            index: _curindex
        });
    };
    addToPlaylist = (_item: QUEUE_ITEM) => {
        let _queue = this.data.queue;
        let _curindex = 0;
        let _wasempty = true;
        if (_queue.length > 0) {
            _curindex = _queue.findIndex(x => x.id === _queue[this.data.index].id);
            _wasempty = false;
        }
        this.updateData({
            ...this.data,
            queue: [..._queue, _item],
            index: _curindex
        });
        if (_wasempty) this.play(true);
    };
    onLoadStartEvent = (ev: Event) => {
        console.log("LOAD START");
        //setStatus("BUFFERING");
    };
    onPlayEvent = (ev: Event) => {
        this.updateData({ ...this.data, status: "PLAYING" });
        this.updateNotification();
    };
    updateNotification = async () => {
        let index = this.data.index;
        let playdata = this.data.queue[index];
        if (typeof playdata === "undefined") return;
        let thumbnail = await storage.ref(`thumbnails/${playdata.artistId}/${playdata.id}.png`).getDownloadURL();
        let artist = "";
        let album = "";
        if (playdata.type === "SONG") {
            artist = playdata.artist;
            album = playdata.album;
        } else {
            artist = playdata.author;
            album = playdata.genre;
        }
        if (navigator.mediaSession) {
            navigator.mediaSession.metadata = new MediaMetadata({
                title: playdata.title,
                artist: artist,
                album: album,
                artwork: [
                    { src: thumbnail, sizes: '300x300', type: 'image/jpg' },
                    { src: thumbnail, sizes: '96x96', type: 'image/png' },
                    { src: thumbnail, sizes: '128x128', type: 'image/png' },
                    { src: thumbnail, sizes: '192x192', type: 'image/png' },
                    { src: thumbnail, sizes: '256x256', type: 'image/png' },
                    { src: thumbnail, sizes: '384x384', type: 'image/png' },
                    { src: thumbnail, sizes: '512x512', type: 'image/png' },
                ]
            });
            navigator.mediaSession.setActionHandler('play', () => this.play);
            navigator.mediaSession.setActionHandler('pause', this.pause);
            navigator.mediaSession.setActionHandler("seekto", (details) => {
                let time = details.seekTime;
                if (!this.audio) return;
                this.seekToTime(time);
            });
            navigator.mediaSession.setActionHandler('previoustrack', this.prev);
            navigator.mediaSession.setActionHandler('nexttrack', this.next);
        }
    };
    onPauseEvent = (ev: Event) => {
        this.updateData({ ...this.data, status: "PAUSED" });
    };
    shuffleQueue = () => {
        let item = this.data.queue[this.data.index];
        let shuffled = shuffleList([...this.data.queue]);
        let index = shuffled.findIndex(x => x.id === item.id);
        this.updateData({ ...this.data, queue: shuffled, index: index });
    };
    playItemFromQueue = (item: QUEUE_ITEM) => {
        let index = this.data.queue.findIndex(x => x.id === item.id);
        if (index < 0) return;
        this.updateData({ ...this.data, index: index });
        this.play(true);
    };
    seekToTime = (time: number) => {
        this.audio.currentTime = time;
    };
    onTimeUpdate = (ev: Event) => {
        this.updateData({
            ...this.data,
            stats: {
                max: this.audio.duration,
                min: 0,
                current: this.audio.currentTime,
                text: timetext(this.audio.currentTime)
            }
        });
    };
    onEndedEvent = (ev: Event) => {
        if (this.data.repeat === "ONE") this.play();
        else this.next();
    };
    addEventListeners = () => {
        this.audio.addEventListener("loadstart", this.onLoadStartEvent);
        this.audio.addEventListener("play", this.onPlayEvent);
        this.audio.addEventListener("pause", this.onPauseEvent);
        this.audio.addEventListener("timeupdate", this.onTimeUpdate);
        this.audio.addEventListener("ended", this.onEndedEvent);
    };
    removeEventListeners = () => {
        this.audio.removeEventListener("loadstart", this.onLoadStartEvent);
        this.audio.addEventListener("play", this.onPlayEvent);
        this.audio.addEventListener("pause", this.onPauseEvent);
        this.audio.addEventListener("timeupdate", this.onTimeUpdate);
    };
    setPlaylist = (queue: QUEUE_ITEM[], name: string, item: QUEUE_ITEM) => {
        let newqueue = queue;
        let index = queue.findIndex(x => x.id === item.id);
        if (index < 0) {
            newqueue = [item];
            index = 0;
        }
        this.updateData({
            ...this.data,
            queue: newqueue,
            index: index,
            name: name
        });
        this.play(true);
    }
};

export function PlayerProvider({ children }: { children: any }) {
    const { currentUser } = useAuth();
    const [player, setPlayer] = React.useState(new PlayerController());
    const [playerdata, setPlayerData] = React.useState<PlayerControllerInterface>(player.data);
    function setRepeat(repeat: "ALL" | "ONE" | undefined) {
        player.updateData({
            ...playerdata,
            repeat: repeat
        });
    };
    React.useEffect(() => {
        player.onUpdate = setPlayerData;
        return player.removeEventListeners;
    }, [player]);
    let value: PlayerContextType = {
        repeat: playerdata.repeat,
        setRepeat,
        stats: playerdata.stats,
        status: playerdata.status,
        currentIndex: playerdata.index,
        active: playerdata.queue.length > 0,
        playlistName: playerdata.name,
        queue: playerdata.queue,
        play: player.play,
        pause: player.pause,
        next: player.next,
        prev: player.prev,
        seek: player.seekToTime,
        setPlaylist: player.setPlaylist,
        shuffle: player.shuffleQueue,
        playItemFromQueue: player.playItemFromQueue,
        pauseorplay: player.pauseorplay,
        removeoraddtoplaylist: player.removeoraddtoplaylist
    };
    React.useEffect(() => {
        if (currentUser === null) {
            player.pause();
            setPlayer(new PlayerController());
            setPlayerData(player.data);
        };
    }, [currentUser]);
    return (
        <PlayerContext.Provider value={value}>
            {children}
        </PlayerContext.Provider>
    );
};